import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ApiError } from "@eduviaos/shared";
import { apiErrorMessage } from "../../lib/apiErrorCopy";
import { Button, cn, Sheet, SheetDescription, SheetHeader, SheetTitle } from "@eduviaos/ui-kit";
import { QuestionCountPicker } from "../../components/sc/QuestionCountPicker";
import { clampQuestionCount } from "../../lib/learning/questionCount";
import { ResponsiveSheetContent } from "../../components/ResponsiveSheetContent";
import { SectionCard } from "../../components/Section";
import { Ziggy } from "../../components/Ziggy";
import type { ZiggyAge } from "../../lib/ziggy";
import { useLearnLocale } from "../../lib/i18n/useLearnLocale";
import {
  useLearningPreferences,
  useReviewCard,
  useStartPostReviewQuiz,
} from "../../lib/learning/hooks";
import type { PostReviewQuizSession, ReviewCard } from "../../lib/learning/types";
import { PostReviewQuizRunner } from "./PostReviewQuizRunner";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

function reviewGrades(strings: ReturnType<typeof useLearnLocale>["strings"]) {
  return [
    {
      quality: 0,
      label: strings.gradeBlanked,
      hint: strings.gradeBlankedHint,
      tone: "border-destructive/40 text-destructive hover:bg-destructive/10",
    },
    {
      quality: 3,
      label: strings.gradeShaky,
      hint: strings.gradeShakyHint,
      tone: "border-joy-amber/50 text-joy-amber hover:bg-joy-amber/10",
    },
    {
      quality: 4,
      label: strings.gradeSolid,
      hint: strings.gradeSolidHint,
      tone: "border-joy-green/50 text-joy-green hover:bg-joy-green/10",
    },
    {
      quality: 5,
      label: strings.gradeInstant,
      hint: strings.gradeInstantHint,
      tone: "border-primary/50 text-primary hover:bg-primary/10",
    },
  ] as const;
}

/**
 * The day's review queue (Phase SC-3d).
 *
 * A card is a **retrieval cue, not a flashcard** — the backend stores a
 * subject and a topic, no question text and no answer. So the screen asks
 * the learner to recall the topic from their own material and then rate
 * how that went; it never displays study content, because inventing
 * content the backend doesn't have is exactly the mock-data trap SC-3b
 * removed from this tab.
 *
 * The queue is snapshotted on mount and walked by local index. Grading a
 * card invalidates the due query, so a live prop would shrink under the
 * session and end it after the first card.
 */
export function ReviewSession({
  cards,
  ziggyAge = "grown",
  onClose,
}: {
  cards: ReviewCard[];
  ziggyAge?: ZiggyAge;
  onClose: () => void;
}) {
  const reduce = useReducedMotion();
  const { strings, locale } = useLearnLocale();
  const grades = reviewGrades(strings);
  const prefsQuery = useLearningPreferences();
  const prefs = prefsQuery.data;
  const maxPostReview = Math.min(5, prefs?.max_questions_per_quiz ?? 5);
  const defaultPostReview = prefs?.default_post_review_question_count ?? 3;

  const reviewMut = useReviewCard();
  const startPostQuizMut = useStartPostReviewQuiz();

  const [queue] = React.useState(() => cards);
  const [index, setIndex] = React.useState(0);
  const [revealed, setRevealed] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [postQuizSession, setPostQuizSession] = React.useState<PostReviewQuizSession | null>(null);
  const [grading, setGrading] = React.useState(false);
  const [postQuizPickerOpen, setPostQuizPickerOpen] = React.useState(false);
  const [pendingCardId, setPendingCardId] = React.useState<string | null>(null);
  const [postQuizError, setPostQuizError] = React.useState<string | null>(null);
  const [postReviewCount, setPostReviewCount] = React.useState(defaultPostReview);

  React.useEffect(() => {
    setPostReviewCount(defaultPostReview);
  }, [defaultPostReview]);

  const total = queue.length;
  const card = queue[index];

  if (!card && !postQuizSession) {
    return (
      <SessionComplete ziggyAge={ziggyAge} reviewed={index} strings={strings} onClose={onClose} />
    );
  }

  if (postQuizSession) {
    return (
      <PostReviewQuizRunner
        session={postQuizSession}
        onComplete={() => {
          setPostQuizSession(null);
          setRevealed(false);
          setIndex((i) => i + 1);
        }}
      />
    );
  }

  if (!card) {
    return (
      <SessionComplete ziggyAge={ziggyAge} reviewed={index} strings={strings} onClose={onClose} />
    );
  }

  async function startPostQuiz(cardId: string) {
    setError(null);
    setPostQuizError(null);
    try {
      const session = await startPostQuizMut.mutateAsync({
        cardId,
        body: {
          question_count: clampQuestionCount(postReviewCount, 1, maxPostReview),
          difficulty: "medium",
        },
      });
      setPostQuizPickerOpen(false);
      setPendingCardId(null);
      setPostQuizSession(session);
    } catch (err) {
      const unavailable =
        err instanceof ApiError && (err.status === 503 || err.code === "AI_UNAVAILABLE");
      setPostQuizError(
        unavailable
          ? strings.postQuizUnavailable
          : apiErrorMessage(err, locale, strings, strings.postQuizStartFailed),
      );
    }
  }

  async function grade(quality: number) {
    setError(null);
    setGrading(true);
    const current = queue[index];
    if (!current) {
      setGrading(false);
      return;
    }
    try {
      const reviewed = await reviewMut.mutateAsync({ cardId: current.id, quality });
      if (reviewed.post_quiz_required !== false) {
        setPendingCardId(current.id);
        setPostQuizPickerOpen(true);
      } else {
        setRevealed(false);
        setIndex((i) => i + 1);
      }
    } catch (err) {
      setError(apiErrorMessage(err, locale, strings, strings.reviewSaveFailed));
    } finally {
      setGrading(false);
    }
  }

  const progress = Math.round((index / total) * 100);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onClose}
          disabled={reviewMut.isPending || grading || startPostQuizMut.isPending}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm font-medium transition-colors disabled:opacity-50"
        >
          <ArrowLeft className="size-4" />
          {strings.finishLater}
        </button>
        <span className="text-muted-foreground text-xs font-semibold tabular-nums">
          {index + 1} / {total}
        </span>
      </div>

      <div
        className="bg-muted h-2 overflow-hidden rounded-full"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={index}
        aria-label={strings.reviewProgressAria(index + 1, total)}
      >
        <motion.div
          className="bg-primary h-full rounded-full"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: reduce ? 0 : 0.3, ease: EASE_OUT }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={card.id}
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -12 }}
          transition={{ duration: 0.24, ease: EASE_OUT }}
          className="space-y-4"
        >
          <SectionCard className="space-y-3 py-6 text-center">
            <span className="bg-primary/12 text-primary inline-block rounded-full px-2.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wide">
              {card.subject}
            </span>
            <p className="font-display text-xl font-semibold">{card.topic}</p>
            <p className="text-muted-foreground mx-auto max-w-xs text-xs leading-relaxed">
              {strings.recallPrompt}
            </p>
            {card.pathway_title && (
              <p className="text-muted-foreground/70 text-[0.68rem]">
                {strings.fromPathway(card.pathway_title)}
              </p>
            )}
          </SectionCard>

          {revealed ? (
            <div className="grid grid-cols-2 gap-2">
              {grades.map((g) => (
                <button
                  key={g.quality}
                  type="button"
                  disabled={reviewMut.isPending || grading || startPostQuizMut.isPending}
                  onClick={() => void grade(g.quality)}
                  className={cn(
                    "bg-card flex flex-col items-center gap-0.5 rounded-xl border px-3 py-3 transition-colors duration-150 active:scale-[0.98] disabled:opacity-50",
                    g.tone,
                  )}
                >
                  <span className="text-sm font-semibold">{g.label}</span>
                  <span className="text-muted-foreground text-[0.68rem]">{g.hint}</span>
                </button>
              ))}
            </div>
          ) : (
            <Button className="w-full" onClick={() => setRevealed(true)}>
              {strings.iveTriedRateRecall}
            </Button>
          )}
        </motion.div>
      </AnimatePresence>

      {(reviewMut.isPending || grading || startPostQuizMut.isPending) && (
        <p className="text-muted-foreground flex items-center justify-center gap-1.5 text-xs">
          <Loader2 className="size-3.5 animate-spin" />{" "}
          {startPostQuizMut.isPending ? strings.buildingFollowUpQuiz : strings.saving}
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="bg-destructive/10 text-destructive rounded-lg px-3 py-2 text-center text-sm font-medium"
        >
          {error}
        </p>
      )}

      <Sheet
        open={postQuizPickerOpen}
        onOpenChange={(next) => {
          if (next) setPostQuizPickerOpen(true);
        }}
      >
        <ResponsiveSheetContent
          showClose={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>{strings.postReviewQuestions}</SheetTitle>
            <SheetDescription>{strings.postReviewHint}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <QuestionCountPicker
              id="post-review-count"
              label={strings.questions}
              value={postReviewCount}
              min={1}
              max={maxPostReview}
              onChange={setPostReviewCount}
            />
            {postQuizError ? (
              <motion.p
                role="alert"
                initial={reduce ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={reduce ? { duration: 0 } : { duration: 0.2, ease: EASE_OUT }}
                className="bg-destructive/10 text-destructive rounded-lg px-3 py-2 text-center text-sm font-medium"
              >
                {postQuizError}
              </motion.p>
            ) : null}
            <Button
              type="button"
              className="w-full"
              disabled={!pendingCardId || startPostQuizMut.isPending}
              onClick={() => pendingCardId && void startPostQuiz(pendingCardId)}
            >
              {startPostQuizMut.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {strings.buildingQuiz}
                </>
              ) : postQuizError ? (
                strings.retry
              ) : (
                strings.startFollowUp
              )}
            </Button>
          </div>
        </ResponsiveSheetContent>
      </Sheet>
    </div>
  );
}

function SessionComplete({
  ziggyAge,
  reviewed,
  strings,
  onClose,
}: {
  ziggyAge: ZiggyAge;
  reviewed: number;
  strings: ReturnType<typeof useLearnLocale>["strings"];
  onClose: () => void;
}) {
  const reduce = useReducedMotion();

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={reduce ? { duration: 0 } : { duration: 0.32, ease: EASE_OUT }}
      className="flex flex-col items-center gap-4 py-6 text-center"
    >
      <Ziggy pose="excited" age={ziggyAge} size={140} />
      <div>
        <p className="font-display text-xl font-semibold">{strings.queueCleared}</p>
        <p className="text-muted-foreground mx-auto mt-1 max-w-xs text-sm">
          {strings.queueClearedHint(reviewed)}
        </p>
      </div>
      <Button onClick={onClose}>{strings.backToPlans}</Button>
    </motion.div>
  );
}
